import { Component } from "@theme/component";
import { bundleState } from "@theme/nugget-bundle";

/**
 * Custom element for the nugget product card with bundle flow.
 *
 * @typedef {object} Refs
 * @property {import('@theme/dialog').DialogComponent} bundleDialogComponent - The nested dialog component
 * @property {HTMLElement} confirmationView - The confirmation view element
 *
 * @extends {Component<Refs>}
 */
class NuggetProductCard extends Component {
  get productId() {
    return this.dataset.productId;
  }

  get variantId() {
    return this.dataset.variantId;
  }

  /**
   * Opens the bundle popup dialog and shows the confirmation view.
   */
  openBundlePopup() {
    // Open the dialog
    this.refs.bundleDialogComponent?.showDialog();

    // Track analytics
    requestAnimationFrame(() => {
      this.#trackModalView("confirmation");
    });
  }

  /**
   * Closes the bundle popup dialog.
   * @returns {Promise<void>}
   */
  closeBundlePopup() {
    return this.refs.bundleDialogComponent?.closeDialog() ?? Promise.resolve();
  }

  /**
   * Requests the universal cancellation modal (handled by the bundle banner).
   * Used when user closes the confirmation dialog.
   */
  async requestCancellation() {
    await this.closeBundlePopup();
    this.dispatchEvent(
      new CustomEvent("bundle:show-cancellation", { bubbles: true }),
    );
  }

  /**
   * Starts the nugget bundle flow
   */
  async startBundleFlow() {
    await this.closeBundlePopup();
    bundleState.setBundleFlow(true, this.id);
    this.#trackBundleFlowEntry();

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }

  // TODO: Implement Analytics Tracking

  /**
   * @param {"confirmation" | "cancellation"} viewType
   */
  #trackModalView(viewType) {
    if (viewType === "confirmation") {
      window.grAnalytics?.trackBundleSuccessModalViewed({});
      return;
    }

    window.grAnalytics?.trackBundleWarningModalViewed({});
  }

  #trackBundleFlowEntry() {
    window.grAnalytics?.trackBundleFlowEntry({});
  }

}

if (!customElements.get("nugget-product-card")) {
  customElements.define("nugget-product-card", NuggetProductCard);
}

export { NuggetProductCard };
